import Button from '@mui/material/Button'
import { styled, useTheme } from '@mui/material/styles'
import React from 'react'
import { FormattedMessage } from 'react-intl'
import { useSpring, animated } from 'react-spring'
import Step from '../../constants/Step'
import { OrderProduct } from '../../entities/order-product/OrderProduct'
import ProductControlSwitcher from '../product-control-switcher/ProductControlSwitcher'
import ProductDeclineButton from '../product-decline-button/ProductDeclineButton'
import messages from './ProductItemControls.messages'

export interface IOwnProps {
  product: OrderProduct
}

export interface IStateProps {
  notPreparedOrderId?: number
}

export interface IDispatchProps {
  requestProductAccept: (product: OrderProduct) => void
  requestPrint: (product: OrderProduct) => void
  resetFailedOrderId: () => void
}

type IProps = IOwnProps & IStateProps & IDispatchProps

const PrintButton = animated(styled(Button)({
  width: 80,
}))

const ProductItemControls: React.FC<IProps> = (props) => {
  const {
    product,
    notPreparedOrderId,
    requestProductAccept,
    requestPrint,
    resetFailedOrderId,
  } = props

  const shouldHighlightPrintButton = notPreparedOrderId === product.order?.orderId && product.order?.isInProgress()

  const [highlightedPrintButtonStyles] = useSpring({
    x: shouldHighlightPrintButton ? 1 : 0,
    config: {
      duration: 400,
    },
    onResolve: () => shouldHighlightPrintButton && resetFailedOrderId(),
  }, [notPreparedOrderId])
  const theme = useTheme()

  const handleAcceptClick = () => {
    requestProductAccept(product)
  }

  const handlePrintClick = () => {
    requestPrint(product)
  }

  if (product.canPrint()) {
    return (
      <PrintButton
        onClick={handlePrintClick}
        variant='contained'
        color='primary'
        style={{
          transform: highlightedPrintButtonStyles.x
            .to({
              range: [0, 0.30, 0.65, 1],
              output: [0, 14, 10, 12],
            })
            .to(x => `translateX(${x}px)`),
          color: highlightedPrintButtonStyles.x
            .to({
              range: [0, 0.05, 1],
              output: [
                theme.palette.text.secondary,
                theme.palette.error.main,
                theme.palette.error.main,
              ],
            }),
        }}
      >
        <FormattedMessage {...messages.print} />
      </PrintButton>
    )
  }

  return (
    <>
      {
        product.order?.cycle?.column?.step === Step.INCOMING
        &&
        <ProductControlSwitcher
          initialContent={() => (
            product.canMoveToInProgress() && !product.isDeclined
            &&
            <Button
              onClick={handleAcceptClick}
              variant='contained'
              color='primary'
            >
              <FormattedMessage {...messages.accept} />
            </Button>
          )}
          toggleContent={() => (
            <>
              {product.canMoveToInProgress() && product.isDeclined
                &&
                <Button
                  onClick={handleAcceptClick}
                  variant='contained'
                  color='secondary'
                >
                  <FormattedMessage {...messages.accept} />
                </Button>
              }

              {product.canDecline()
                && <ProductDeclineButton product={product} />
              }
            </>
          )}
        />
      }
    </>
  )
}

export default ProductItemControls
